<template>
  <!-- Skip navigation link for assistive technologies -->
  <a
    href="#main-content"
    class="skip-link"
  >Skip to main content</a>

  <!-- Desktop logo – absolutely positioned to overlap header and content -->
  <router-link
    v-if="!isCompactNav"
    :to="{ name: 'Home' }"
    class="app-navigation__logo-link d-flex justify-center align-center"
    aria-label="Home"
  >
    <v-sheet
      class="app-navigation__logo"
      color="background"
      height="120"
      width="120"
    >
      <responsive-picture
        v-if="logoPicture"
        :picture="logoPicture"
        alt="logo"
        picture-class="app-navigation__logo-picture"
        image-class="app-navigation__logo-image"
        loading="eager"
      />
    </v-sheet>
  </router-link>

  <v-app-bar
    role="navigation"
    aria-label="Main site navigation"
    :class="['app-navigation__bar', isCompactNav ? 'app-navigation__bar--compact' : 'app-navigation__bar--spacious', 'app-navigation__bar--static']"
    :scroll-behavior="isCompactNav ? 'hide' : 'elevate'"
    color="primary"
    location="top"
  >
    <!-- Mobile logo (shown in compact mode) -->
    <template #title>
      <router-link
        v-if="isCompactNav"
        :to="{ name: 'Home' }"
        class="app-navigation__mobile-logo d-flex justify-center align-center elevation-2"
        aria-label="Home"
      >
        <responsive-picture
          v-if="logoPicture"
          :picture="logoPicture"
          alt="logo"
          picture-class="app-navigation__logo-picture"
          image-class="app-navigation__logo-image"
          loading="eager"
        />
      </router-link>
    </template>

    <!-- Navigation controls -->
    <template #append>
      <v-app-bar-nav-icon
        v-if="isCompactNav"
        variant="text"
        aria-label="Toggle navigation drawer"
        :aria-controls="'nav-drawer'"
        :aria-expanded="drawer"
        @click.stop="drawer = !drawer"
      />

      <v-list
        v-else
        bg-color="transparent"
        class="app-navigation__link-list d-flex align-center justify-end"
        variant="text"
      >
        <app-navigation-links />
      </v-list>
    </template>
    <!-- End of merged app bar -->
  </v-app-bar>

  <v-navigation-drawer
    id="nav-drawer"
    v-model="drawer"
    :temporary="true"
    class="app-navigation__drawer"
    color="primary"
    location="end"
    role="dialog"
    aria-label="Navigation drawer"
  >
    <v-list
      bg-color="transparent"
      class="app-navigation__link-list--drawer d-flex flex-column justify-center align-center"
      variant="text"
    >
      <app-navigation-links @close="drawer = false" />
    </v-list>
  </v-navigation-drawer>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useMobileStore } from '@/stores/displayStore'
import { useAppStore } from '@/stores/app'

import AppNavigationLinks from '@/components/app/navigation/AppNavigationLinks.vue'
import ResponsivePicture from '@/components/ui/ResponsivePicture.vue'
import { useSiteAssets } from '@/composables/useSiteAssets'

const mobileStore = useMobileStore()
const appStore = useAppStore()
const { siteAssets, ensureLoaded } = useSiteAssets()
ensureLoaded()

const drawer = ref(false)
// Use a computed value so the navigation bar updates when the store changes.
const _navHeight = computed(() => appStore.getElementSize().nav)
const logoPicture = computed(() => siteAssets.value?.logo)

const isCompactNav = computed(() => mobileStore.tablet || mobileStore.mobile)

// Automatically close the drawer when navigating to a new route (mobile use case).
const route = useRoute()
watch(
  () => route.fullPath,
  () => {
    if (drawer.value) drawer.value = false
  }
)
</script>

<style scoped>
/* Logo container – absolutely positioned to overlap header and content */
.app-navigation__logo {
  width: 120px;
  height: 120px;
  overflow: hidden;
  border-radius: 50%;
  box-shadow: 0 0 10px 1px rgba(0, 0, 0, 0.75);
}

.app-navigation__logo-link {
  text-decoration: none;
  position: absolute;
  z-index: 20000;
  top: 30px;
  left: 100px;
}

/* Visually hidden skip link – becomes visible on focus */
.skip-link {
  position: absolute;
  left: -999px;
  top: -999px;
  background: #fff;
  color: #000;
  padding: 0.5rem;
  z-index: 10000;
}
.skip-link:focus {
  left: 1rem;
  top: 1rem;
}

/* Use :deep() to style elements inside child component (ResponsivePicture) */
:deep(.app-navigation__logo-picture) {
  display: block;
  width: 100%;
  height: 100%;
}

.app-navigation__mobile-logo {
  width: 50px;
  height: 50px;
  overflow: hidden;
  border-radius: 50%;
  z-index: 20000;
}

:deep(.app-navigation__logo-image) {
  border-radius: 50%;
  cursor: pointer;
  display: block;
  width: 90%;
  height: 90%;
  margin: 5%;
  object-fit: cover;
}

/* Increase right padding on desktop navigation bar for better spacing */
.app-navigation__bar--spacious {
  padding: 28px 120px 28px 100px; /* top/right/bottom/left */
}

.app-navigation__bar--compact {
  padding: 20px;
}

/* Make the bar part of normal flow – it scrolls with content */
.app-navigation__bar--static {
  position: static !important;
}

.app-navigation__link-list {
  font-size: 13px;
  gap: 35px;
  padding-right: 1rem;
}

.app-navigation__link-list--drawer {
  width: 100%;
}

.app-navigation__drawer {
  padding-top: 120px;
}
</style>
